<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { EntityNewsSkeletonDetails } from '~/components/entity/news/skeleton'
import { WidgetTrendingNews } from '~/components/widget/trending-news'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetNewsPageDetails } from '~/components/widget/news-page'
import type { INews } from '~/components/entity/news'
import { NewsType } from '~/components/entity/news/types'
import type { IResponseData } from '~/types'
import { WidgetNewsAttachedOrganizations } from '~/components/widget/news-attached-organizations'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'
import { WidgetSidebarNews } from '~/components/widget/sidebar-news'
import { WidgetPoll } from '~/components/widget/poll'
import { FeatureCardFormRequest } from '~/components/feature/card/form-request'
import { FeatureCreditFormRequest } from '~/components/feature/credit/form-request'
import { FeatureDepositFormRequest } from '~/components/feature/deposit/form-request'
import { WidgetAuthor } from '~/components/widget/author'
import { WidgetComments } from '~/components/widget/comments'
import { PreviewPlaceholder } from '~/components/ui/preview-placeholder'
import { Disclaimer } from '~/components/ui/disclaimer'
import { AirunDisclaimer } from '~/components/ui/airun'
import { EntityCardItem } from '~/components/entity/card'
import { EntityCreditItem } from '~/components/entity/credit'
import { EntityDepositItem } from '~/components/entity/deposit'
import { EntityMoneyTransferItem } from '~/components/entity/money-transfer'
import { LucideLanguages, LucideArrowRight } from 'lucide-vue-next'

const { t, locale, loadLocaleMessages } = useI18n()
const config = useRuntimeConfig()

const route = useRoute()
const urlParams = `/news/${route.params.slug}`
// A signed link from Nova that opens an inactive news item (#1012). The page is cached
// by its full URL, so the preview never replaces the public page in the cache
const previewToken = typeof route.query.preview === 'string' ? route.query.preview : ''
const { data } = await useApiWith404<IResponseData<INews>>(
  () => useUrlWithParams(urlParams, previewToken ? { preview: previewToken } : undefined),
  {
    key: `news-page.view.${route.params.slug}${previewToken ? '.preview' : ''}`
  }
)
const news: ComputedRef<INews | undefined> = computed(() => data.value?.data)

useNewsPreviewDraft(data, Boolean(previewToken))
const isEmbeddedPreview = useEmbeddedPreview()

if (previewToken) {
  useHead({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] })
}

const altLocale = news.value?.language === 'ru' ? 'ky' : 'ru'
await loadLocaleMessages(altLocale)

const altNewsNotice = computed(() => ({
  description: t('page.news.news_alternative_link_notice.description', {}, { locale: altLocale }),
  link: t('page.news.news_alternative_link_notice.link', {}, { locale: altLocale })
}))

const baseUrl = config.public.siteUrl
useSeoFromEntity(news, baseUrl, locale.value)
useNewsLanguageLinks(news)

if (news.value) {
  const author = news.value.user
  const images = [news.value.largeImage, news.value.picture].filter(Boolean)

  useSchemaOrg([
    defineArticle({
      '@type': news.value.type === NewsType.article ? 'Article' : 'NewsArticle',
      headline: news.value.title,
      description: useStripTags(news.value.summary) || undefined,
      image: images.length ? images : undefined,
      datePublished: news.value.publication_datetime || undefined,
      dateModified: news.value.updated_at || undefined,
      inLanguage: news.value.language || locale.value,
      author: author?.fullname
        ? {
            '@type': 'Person',
            name: author.fullname,
            url:
              author.url && author.settings?.public_profile
                ? `${baseUrl}${author.url}`
                : undefined
          }
        : undefined
    })
  ])
}

const breadcrumbs = computed(() => {
  if (news.value?.category) {
    return [
      { title: t('page.news.breadcrumb'), url: '/news' },
      { title: news.value.categories[0].title, url: news.value.categories[0].url },
      {
        title: news?.value?.title || '',
        url: news?.value?.url || ''
      }
    ]
  } else {
    return [
      { title: t('page.news.breadcrumb'), url: '/news' },
      {
        title: news?.value?.title || '',
        url: news?.value?.url || ''
      }
    ]
  }
})

const relatedOrganizations = computed(() => {
  return news.value?.organizations?.map((org) => org.id) || []
})

const relatedTags = computed(() => {
  return news.value?.tags?.map((tag) => tag.slug) || []
})

const leadRelation = computed(() => {
  return news.value?.lead_relation || null
})

// The API returns `ratings` as an object, while the item components expect a
// scalar. List widgets flatten it the same way before rendering an item.
const leadRelationModel = computed(() => {
  if (!leadRelation.value?.model) return null

  return {
    ...leadRelation.value.model,
    ratings: leadRelation.value.model.ratings?.average_rate ?? null
  }
})

const leadRelationCardRequestFormTitle = computed(() => {
  if (leadRelation.value?.model_id) {
    return t('page.news.card_request_with_model', {
      model: leadRelation.value.model.title,
      organization: leadRelation.value.model.organization.title
    })
  } else if (leadRelation.value?.model) {
    return t('page.news.card_request_without_model', {
      organization: leadRelation.value.model.organization.title
    })
  } else {
    return ''
  }
})

const leadRelationDepositRequestFormTitle = computed(() => {
  if (leadRelation.value?.model_id) {
    return t('page.news.deposit_request_with_model', {
      model: leadRelation.value.model.title,
      organization: leadRelation.value.model.organization.title
    })
  } else if (leadRelation.value?.model) {
    return t('page.news.deposit_request_without_model', {
      organization: leadRelation.value.model.organization.title
    })
  } else {
    return ''
  }
})

const leadRelationCreditRequestFormTitle = computed(() => {
  if (leadRelation.value?.model_id) {
    return t('page.news.credit_request_with_model', {
      model: leadRelation.value.model.title,
      organization: leadRelation.value.model.organization.title
    })
  } else if (leadRelation.value?.model) {
    return t('page.news.credit_request_without_model', {
      organization: leadRelation.value.model.organization.title
    })
  } else {
    return ''
  }
})

// The page is served through ISR (600s), so the view has to be recorded by the
// browser. Counting during setup() would only fire once per cache rebuild.
const { viewsCount, recordView } = useNewsViews()

onMounted(() => {
  if (news.value?.id && !previewToken) {
    recordView(news.value.id)
  }
})

// Prefer the count returned by the view endpoint — the one embedded in the page
// comes from the ISR snapshot and can be up to 10 minutes stale.
const displayedViewsCount = computed(() => viewsCount.value ?? news.value?.views_count ?? null)
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetTrendingNews />
            <template v-if="news && news.user">
              <WidgetAuthor :author="news.user" :enable-link="true" />
            </template>
            <ClientOnly v-if="!isEmbeddedPreview">
              <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            </ClientOnly>
            <WidgetNewsAttachedOrganizations
              v-if="news && news.organizations.length"
              :organizations="news.organizations"
            />

            <WidgetSidebarNews
              v-if="news && news.organizations && relatedOrganizations.length"
              :heading="$t('page.news.related_news')"
              path="/news"
              :news_id="news.id"
              :query-params="{
                per_page: '5',
                'filter[type]': 'news',
                'filter[organization_id]': relatedOrganizations
              }"
              variant="white"
            />
            <WidgetSidebarNews
              v-if="
                news &&
                news.organizations &&
                relatedOrganizations.length === 0 &&
                news.categories &&
                !news.categories.some((c) => c.slug === 'exchange-rates') &&
                news.tags.length
              "
              :heading="$t('page.news.related_news')"
              path="/news"
              :news_id="news.id"
              :query-params="{
                per_page: '5',
                'filter[type]': 'news',
                'filter[tag]': relatedTags
              }"
              variant="white"
            />
            <WidgetSidebarNews
              v-if="
                news && news.categories && news.categories.some((c) => c.slug === 'exchange-rates')
              "
              :heading="$t('page.news.related_news')"
              path="/news"
              :news_id="news.id"
              :query-params="{
                per_page: '5',
                'filter[category]': 'exchange-rates',
                'filter[type]': 'news',
                sort: '-publication_datetime'
              }"
              variant="white"
            />
          </aside>
        </template>
        <div role="main" class="space-y-5">
          <NuxtLink
            v-if="news && news.alternative_link"
            :to="`/news/${news.alternative_link}`"
            class="group flex items-center gap-3 rounded-xl bg-white px-4 py-3 text-sm text-gray-600 ring-1 ring-gray-200 transition hover:bg-blue-50 hover:text-gray-900"
          >
            <LucideLanguages class="size-4 shrink-0 text-blue-600" />
            <span>{{ altNewsNotice?.description }}</span>
            <span
              class="ml-auto flex shrink-0 items-center gap-1 font-medium text-gray-900 group-hover:underline"
            >
              {{ altNewsNotice?.link }}
              <LucideArrowRight class="size-3.5" />
            </span>
          </NuxtLink>
          <WidgetNewsPageDetails v-if="news" v-bind="news" :views_count="displayedViewsCount" />
          <EntityNewsSkeletonDetails v-else />

          <div v-if="news && news.disclaimers && news.disclaimers.length > 0" class="space-y-5">
            <!-- The airun flag credits a partner service rather than warning the
                 reader, so it gets the branded block instead of the yellow one. -->
            <component
              :is="disclaimerKey === 'airun' ? AirunDisclaimer : Disclaimer"
              v-for="disclaimerKey in news.disclaimers"
              :key="disclaimerKey"
              :title="$t(`page.news.disclaimers.${disclaimerKey}.title`)"
              :description="$t(`page.news.disclaimers.${disclaimerKey}.description`)"
            />
          </div>

          <ClientOnly>
            <ContentBlock v-if="news && news.poll_ids && news.poll_ids.length" variant="white">
              <template v-for="pollId in news.poll_ids" :key="pollId">
                <WidgetPoll :poll-id="pollId" class="w-full" />
              </template>
            </ContentBlock>
          </ClientOnly>
          <template v-if="leadRelation && leadRelationModel && leadRelation.model_type === 'card'">
            <EntityCardItem :item="leadRelationModel" :hide-compare="true" />
            <FeatureCardFormRequest
              :title="leadRelationCardRequestFormTitle"
              heading-tag="h2"
              heading-class="h4"
              :organization-type="leadRelation.organization_type"
              :organization-id="leadRelation.organization_id"
              :card-id="leadRelation.model_id"
            />
          </template>
          <template
            v-if="leadRelation && leadRelationModel && leadRelation.model_type === 'credit'"
          >
            <EntityCreditItem :item="leadRelationModel" :hide-compare="true" />
            <FeatureCreditFormRequest
              v-if="leadRelation.model_id"
              heading-tag="h2"
              heading-class="h4"
              variant="white"
              :heading-text="leadRelationCreditRequestFormTitle"
              :credit="leadRelation.model as any"
            />
            <FeatureCreditFormRequest
              v-else
              heading-tag="h2"
              heading-class="h4"
              variant="white"
              :heading-text="leadRelationCreditRequestFormTitle"
              :organization="(leadRelation.model as any).organization"
            />
          </template>

          <template
            v-if="leadRelation && leadRelationModel && leadRelation.model_type === 'deposit'"
          >
            <EntityDepositItem :item="leadRelationModel" :hide-compare="true" />
            <ContentBlock
              variant="white"
              :heading-text="leadRelationDepositRequestFormTitle"
              heading-tag="h2"
              heading-class="h4"
            >
              <FeatureDepositFormRequest
                :deposit="leadRelation.model as any"
                :available-currencies="leadRelation.model?.currencies"
              />
            </ContentBlock>
          </template>

          <EntityMoneyTransferItem
            v-if="leadRelation && leadRelationModel && leadRelation.model_type === 'money_transfer'"
            :item="leadRelationModel"
          />

          <PreviewPlaceholder
            v-if="isEmbeddedPreview"
            class="h-16 rounded-lg border-2"
            title="Комментарии"
          />
          <WidgetComments
            v-else-if="news"
            commentable-type="news"
            :commentable-id="news.id"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
